/**
 * FormBuilderSidebar Component
 *
 * Contains the field palette for dragging new fields and the properties panel
 * for editing selected field settings.
 */

import { forwardRef, memo, useCallback, useMemo } from 'react';
import { Box, Text, Tabs, ScrollArea, Stack, TextInput, Textarea, Switch, Select, NumberInput, Group, Button, ActionIcon, Tooltip } from '@mantine/core';
import { Icon } from '@/components/ui/icons/Icon';
import {
  useFormBuilderStore,
  selectSelectedField,
  selectSidebarTab,
  selectIsEditMode,
} from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import { FIELD_CATEGORIES, FIELD_TYPE_CONFIGS, DND_ITEM_TYPE, DRAG_SOURCE, COLUMN_SPAN_OPTIONS } from './constants';
import { parseOptionsString, formatOptionsString } from './utils';
import type { FormBuilderSidebarProps, FieldType, SelectOption } from './types';
import classes from './FormBuilder.module.css';

/**
 * FormBuilderSidebar Component
 */
const FormBuilderSidebar = forwardRef<HTMLDivElement, FormBuilderSidebarProps>(
  ({ className }, ref) => {
    const selectedField = useFormBuilderStore(selectSelectedField);
    const sidebarTab = useFormBuilderStore(selectSidebarTab);
    const isEditMode = useFormBuilderStore(selectIsEditMode);
    const { setSidebarTab } = useFormBuilderStore();

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleTabChange = useCallback(
      (value: string | null) => {
        if (value === 'fields' || value === 'properties') {
          setSidebarTab(value);
        }
      },
      [setSidebarTab]
    );

    return (
      <Box ref={ref} className={`${classes.sidebar} ${className || ''}`}>
        <Tabs
          value={sidebarTab}
          onChange={handleTabChange}
          className={classes.sidebarTabs}
        >
          <Tabs.List grow>
            <Tabs.Tab value="fields" leftSection={<Icon name="layoutGrid" size={16} />}>
              Fields
            </Tabs.Tab>
            <Tabs.Tab
              value="properties"
              leftSection={<Icon name="settings" size={16} />}
              disabled={!selectedField}
            >
              Properties
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <ScrollArea className={classes.sidebarContent}>
          {sidebarTab === 'fields' && <FieldPalette isEditMode={isEditMode} />}
          {sidebarTab === 'properties' && selectedField && (
            <FieldProperties field={selectedField} />
          )}
        </ScrollArea>
      </Box>
    );
  }
);

FormBuilderSidebar.displayName = 'FormBuilderSidebar';

/**
 * Field Palette - draggable field type buttons
 */
const FieldPalette = memo(({ isEditMode }: { isEditMode: boolean }) => {
  const { addField, setDraggedFieldType, setIsDraggingFromPalette } = useFormBuilderStore();

  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const handleDragStart = useCallback(
    (e: React.DragEvent, type: FieldType) => {
      if (!isEditMode) {
        e.preventDefault();
        return;
      }

      e.dataTransfer.setData(DND_ITEM_TYPE, JSON.stringify({
        source: DRAG_SOURCE.PALETTE,
        fieldType: type,
      }));
      e.dataTransfer.effectAllowed = 'copy';

      setDraggedFieldType(type);
      setIsDraggingFromPalette(true);

      // Add visual feedback
      (e.target as HTMLElement).classList.add(classes.paletteItemDragging);
    },
    [isEditMode, setDraggedFieldType, setIsDraggingFromPalette]
  );

  const handleDragEnd = useCallback(
    (e: React.DragEvent) => {
      (e.target as HTMLElement).classList.remove(classes.paletteItemDragging);
      setDraggedFieldType(null);
      setIsDraggingFromPalette(false);
    },
    [setDraggedFieldType, setIsDraggingFromPalette]
  );

  const handleClick = useCallback(
    (type: FieldType) => {
      if (isEditMode) {
        addField(type);
      }
    },
    [isEditMode, addField]
  );

  return (
    <Stack gap="lg">
      {FIELD_CATEGORIES.map((category) => (
        <Box key={category.id} className={classes.fieldCategory}>
          <Text className={classes.categoryTitle} style={accessibilityStyles}>{category.label}</Text>
          <Box className={classes.fieldPalette}>
            {category.fields.map((type) => {
              const config = FIELD_TYPE_CONFIGS[type];
              return (
                <Tooltip key={type} label={config.description} position="right">
                  <Box
                    className={classes.paletteItem}
                    draggable={isEditMode}
                    onDragStart={(e) => handleDragStart(e, type)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleClick(type)}
                    data-disabled={!isEditMode}
                  >
                    <Icon name={config.icon} size={20} />
                    <Text className={classes.paletteItemLabel} style={accessibilityStyles}>
                      {config.label}
                    </Text>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}
    </Stack>
  );
});

FieldPalette.displayName = 'FieldPalette';

/**
 * Field Properties Panel
 */
const FieldProperties = memo(
  ({ field }: { field: NonNullable<ReturnType<typeof selectSelectedField>> }) => {
    const { updateField } = useFormBuilderStore();
    const config = FIELD_TYPE_CONFIGS[field.type];

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleChange = useCallback(
      <K extends keyof typeof field>(key: K, value: (typeof field)[K]) => {
        updateField(field.id, { [key]: value });
      },
      [field.id, updateField]
    );

    const optionsText = useMemo(() => {
      return formatOptionsString(field.options || []);
    }, [field.options]);

    const handleOptionsChange = useCallback(
      (text: string) => {
        const options = parseOptionsString(text);
        updateField(field.id, { options });
      },
      [field.id, updateField]
    );

    return (
      <Stack gap="md" className={classes.settingsPanel}>
        {/* Basic Settings */}
        <Box className={classes.settingsSection}>
          <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Basic Settings</Text>

          <Stack gap="sm">
            <TextInput
              label="Label"
              value={field.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Field label"
            />

            <TextInput
              label="Field Name"
              value={field.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="field_name"
              description="Used as the key in form data"
            />

            {!config.isLayoutElement && (
              <>
                <TextInput
                  label="Placeholder"
                  value={field.placeholder || ''}
                  onChange={(e) => handleChange('placeholder', e.target.value)}
                  placeholder="Placeholder text"
                />

                <Textarea
                  label="Description"
                  value={field.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Help text for users"
                  rows={2}
                />
              </>
            )}
          </Stack>
        </Box>

        {/* Layout Settings */}
        <Box className={classes.settingsSection}>
          <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Layout</Text>

          <Stack gap="sm">
            <Select
              label="Column Span"
              value={String(field.colSpan || 1)}
              onChange={(value) => handleChange('colSpan', Number(value) as 1 | 2 | 3 | 4)}
              data={COLUMN_SPAN_OPTIONS}
            />
          </Stack>
        </Box>

        {/* Field Type Specific Settings */}
        {config.hasOptions && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Options</Text>
            <Textarea
              value={optionsText}
              onChange={(e) => handleOptionsChange(e.target.value)}
              placeholder="value|Label&#10;option1|Option 1&#10;option2|Option 2"
              description="One option per line. Format: value|Label or just value"
              rows={5}
            />
          </Box>
        )}

        {config.hasMinMax && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Range</Text>
            <Group grow>
              <NumberInput
                label="Min"
                value={field.min}
                onChange={(value) => handleChange('min', Number(value))}
              />
              <NumberInput
                label="Max"
                value={field.max}
                onChange={(value) => handleChange('max', Number(value))}
              />
              {field.type === 'slider' && (
                <NumberInput
                  label="Step"
                  value={field.step || 1}
                  onChange={(value) => handleChange('step', Number(value))}
                />
              )}
            </Group>
          </Box>
        )}

        {field.type === 'textarea' && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Textarea Settings</Text>
            <NumberInput
              label="Rows"
              value={field.rows || 4}
              onChange={(value) => handleChange('rows', Number(value))}
              min={1}
              max={20}
            />
          </Box>
        )}

        {field.type === 'heading' && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Heading Settings</Text>
            <Select
              label="Heading Level"
              value={String(field.headingLevel || 2)}
              onChange={(value) => handleChange('headingLevel', Number(value) as 1 | 2 | 3 | 4 | 5 | 6)}
              data={[
                { value: '1', label: 'H1 - Largest' },
                { value: '2', label: 'H2 - Large' },
                { value: '3', label: 'H3 - Medium' },
                { value: '4', label: 'H4 - Small' },
                { value: '5', label: 'H5 - Smaller' },
                { value: '6', label: 'H6 - Smallest' },
              ]}
            />
          </Box>
        )}

        {field.type === 'paragraph' && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Content</Text>
            <Textarea
              value={field.content || ''}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder="Enter paragraph text..."
              rows={4}
            />
          </Box>
        )}

        {field.type === 'spacer' && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Spacer Settings</Text>
            <NumberInput
              label="Height (px)"
              value={field.spacerHeight || 24}
              onChange={(value) => handleChange('spacerHeight', Number(value))}
              min={8}
              max={200}
            />
          </Box>
        )}

        {(field.type === 'file' || field.type === 'image') && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>File Settings</Text>
            <Stack gap="sm">
              <TextInput
                label="Accepted File Types"
                value={field.accept || ''}
                onChange={(e) => handleChange('accept', e.target.value)}
                placeholder="e.g., .pdf,.doc,.docx"
                description={field.type === 'image' ? 'Default: image/*' : 'Comma-separated extensions'}
              />
              <Switch
                label="Allow Multiple Files"
                checked={field.multiple || false}
                onChange={(e) => handleChange('multiple', e.currentTarget.checked)}
              />
            </Stack>
          </Box>
        )}

        {/* Validation Settings (for non-layout elements) */}
        {!config.isLayoutElement && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Validation</Text>
            <Stack gap="sm">
              <Switch
                label="Required"
                checked={field.required || false}
                onChange={(e) => handleChange('required', e.currentTarget.checked)}
              />
              <Switch
                label="Disabled"
                checked={field.disabled || false}
                onChange={(e) => handleChange('disabled', e.currentTarget.checked)}
              />
              <Switch
                label="Read Only"
                checked={field.readOnly || false}
                onChange={(e) => handleChange('readOnly', e.currentTarget.checked)}
              />
            </Stack>
          </Box>
        )}

        {/* Default Value (for applicable types) */}
        {!config.isLayoutElement && field.type !== 'file' && field.type !== 'image' && (
          <Box className={classes.settingsSection}>
            <Text className={classes.settingsSectionTitle} style={accessibilityStyles}>Default Value</Text>
            <DefaultValueInput field={field} onChange={handleChange} />
          </Box>
        )}
      </Stack>
    );
  }
);

FieldProperties.displayName = 'FieldProperties';

/**
 * Default value input based on field type
 */
const DefaultValueInput = memo(
  ({
    field,
    onChange,
  }: {
    field: NonNullable<ReturnType<typeof selectSelectedField>>;
    onChange: <K extends keyof typeof field>(key: K, value: (typeof field)[K]) => void;
  }) => {
    switch (field.type) {
      case 'checkbox':
      case 'switch':
        return (
          <Switch
            label="Default checked"
            checked={Boolean(field.defaultValue)}
            onChange={(e) => onChange('defaultValue', e.currentTarget.checked)}
          />
        );

      case 'select':
        return (
          <Select
            placeholder="Select default option"
            value={(field.defaultValue as string) || ''}
            onChange={(value) => onChange('defaultValue', value || '')}
            data={field.options || []}
            clearable
          />
        );

      case 'number':
      case 'slider':
      case 'rating':
        return (
          <NumberInput
            placeholder="Default value"
            value={field.defaultValue as number}
            onChange={(value) => onChange('defaultValue', Number(value))}
            min={field.min}
            max={field.max}
          />
        );

      case 'textarea':
      case 'richtext':
        return (
          <Textarea
            placeholder="Default text"
            value={(field.defaultValue as string) || ''}
            onChange={(e) => onChange('defaultValue', e.target.value)}
            rows={3}
          />
        );

      default:
        return (
          <TextInput
            placeholder="Default value"
            value={(field.defaultValue as string) || ''}
            onChange={(e) => onChange('defaultValue', e.target.value)}
          />
        );
    }
  }
);

DefaultValueInput.displayName = 'DefaultValueInput';

export default memo(FormBuilderSidebar);
